/**
 *
 * v.0.1  10/09/25  Inicial
 * v.0.2  18/09/25  Añadido efecto Zebra en rows.
 *                  Añadido evento estilizadorRow
 *                  Añadido resize de columnas:
 *                  Añadido autoResizeCol.
 *                  Añadida la opción de ordenación, con ordenado personalizado por tipo de columna
 * v.0.3  09/10/25  Corrección: reintroducidas las funciones auxiliares y columnas fijas
 */

import React from 'react'
import PropTypes from 'prop-types';
import {
    CELLTIPO_CADENA,
    CELLTIPO_ELIMINARROW,
    CELLTIPO_ESTADOROW,
    CELLTIPO_FECHA,
    CELLTIPO_ICONOACCION,
    CELLTIPO_MEMO
} from "./TiposCeldas";
import {getValorCampo} from "../Utils/FormularioUtils";
import {Icon} from "@blueprintjs/core";

const filasZebraColoresDefecto= ["#f9f9f9", "#ffffff"];

class Tabla extends React.Component {
    constructor(props) {
        super(props)

        const columnas = props.columnas ? this.prepararColumnas(props) : [];

        this.state = {
            columnas,
            datos: props.datos ? props.datos : [],
            colWidths: columnas.map(c => c.ancho || 150),
            resizing: null,
            hoverCol: null,
            sortConfig: null,
            elementoSeleccionadoCodigo: null,
        }
    }

    componentDidUpdate(prevProps) {
        if (prevProps.datos !== this.props.datos) {
            this.setState({ datos: this.props.datos });
        }
    }

    prepararColumnas = (props) => {
        let columnaE= { campo: "estadoRow", titulo:"E", name: "E", minWidth: 10, ancho: 30, tipo: CELLTIPO_ESTADOROW, resizable: false, fija: true,};
        let newColumnas = [columnaE, ...props.columnas];

        if (Boolean(props.mostrarEliminar)){
            let columnaEliminar= { campo: "eliminarRow", titulo: "X", name: "X", minWidth: 10, ancho: 30, tipo: CELLTIPO_ELIMINARROW, resizable: false};
            newColumnas.push(columnaEliminar);
        }
        return newColumnas;
    }

    // ************************************************
    // REDIMENSIONAMIENTO DE COLUMNAS
    // ************************************************

    handleMouseMoveTh = (e, index) => {
        if (this.state.resizing)
            return; // no cambiar el cursor mientras se redimensiona

        const th = e.currentTarget;
        const rect = th.getBoundingClientRect();
        const offsetX = e.clientX - rect.left;
        const edgeThreshold = 5; // píxeles desde el borde derecho

        if (rect.width - offsetX <= edgeThreshold) {
            th.style.cursor = "col-resize"; // cambiar cursor
            this.setState({ hoverCol: index });
        } else {
            th.style.cursor = "";
            this.setState({ hoverCol: null });
        }
    }

    handleMouseDownTh = (e, index) => {
        if (this.state.hoverCol === index) {
            this.setState({
                resizing: {
                    colIndex: index,
                    startX: e.clientX,
                    startWidth: this.state.colWidths[index]
                }
            });

            // Cambiar cursor a nivel global
            document.body.style.cursor = "col-resize";

            document.addEventListener("mousemove", this.onResize);
            document.addEventListener("mouseup", this.finalizarResize);
        }
    }

    onResize = (e) => {
        const { resizing, colWidths } = this.state;
        if (!resizing) return;

        const deltaX = e.clientX - resizing.startX;
        const newWidths = [...colWidths];
        newWidths[resizing.colIndex] = Math.max(50, resizing.startWidth + deltaX);
        this.setState({ colWidths: newWidths });
    }

    finalizarResize = () => {
        this.setState({ resizing: null });
        document.body.style.cursor = "";
        document.removeEventListener("mousemove", this.onResize);
        document.removeEventListener("mouseup", this.finalizarResize);
    }

    autoResizeCol = (e, index) => {
        const { columnas, datos, colWidths } = this.state;
        const columna = columnas[index];
        const th = e.currentTarget;
        const rect = th.getBoundingClientRect();
        const offsetX = e.clientX - rect.left;
        const edgeThreshold = 5;

        if (rect.width - offsetX <= edgeThreshold) {
            const table = th.closest("table");
            if (!table) return;

            const tds = Array.from(table.querySelectorAll(`tbody tr td:nth-child(${index + 1})`));
            let maxWidth = th.scrollWidth; // ancho del encabezado
            tds.forEach((td, rowIndex) => {
                let valor = getValorCampo(datos[rowIndex], columna.campo, "");
                const computedStyle = window.getComputedStyle(td);

                const div = document.createElement("div");
                div.style.visibility = "hidden";
                div.style.position = "absolute";
                div.style.whiteSpace = columna.tipo === CELLTIPO_MEMO ? "pre-wrap" : "pre";
                div.style.fontSize = computedStyle.fontSize;
                div.style.fontFamily = computedStyle.fontFamily;
                div.style.fontWeight = computedStyle.fontWeight;
                div.style.padding = computedStyle.padding;
                div.style.letterSpacing = computedStyle.letterSpacing;
                div.style.boxSizing = computedStyle.boxSizing;
                div.innerText = valor;

                document.body.appendChild(div);
                const width = div.offsetWidth;
                if (width > maxWidth) maxWidth = width;
                document.body.removeChild(div);
            });

            const newWidths = [...colWidths];
            newWidths[index] = Math.max(maxWidth, this.state.columnas[index].minWidth || 30);
            this.setState({ colWidths: newWidths });
        }
    }

    // ************************************************
    // ORDENAMIENTO
    // ************************************************

    handleSort = (columna) => {
        const { sortConfig, datos } = this.state;

        let direction = "asc";
        if (sortConfig && sortConfig.campo === columna.campo && sortConfig.direction === "asc") {
            direction = "desc";
        }

        const comparador = this.comparadorPorTipo(columna.tipo, direction);

        const sortedDatos = [...datos].sort((a, b) => comparador(a, b, columna.campo));

        this.setState({
            datos: sortedDatos,
            sortConfig: { campo: columna.campo, direction }
        });
    }

    comparadorPorTipo = (tipo, direction) => {
        return (a, b, campo) => {
            let valorA = getValorCampo(a, campo, "");
            let valorB = getValorCampo(b, campo, "");

            if (valorA === null || valorA === undefined) valorA = "";
            if (valorB === null || valorB === undefined) valorB = "";

            switch (tipo) {
                case CELLTIPO_FECHA: {
                    const fechaA = new Date(valorA).getTime() || 0;
                    const fechaB = new Date(valorB).getTime() || 0;
                    return direction === "asc" ? fechaA - fechaB : fechaB - fechaA;
                }
                case CELLTIPO_CADENA:
                case CELLTIPO_MEMO: {
                    const strA = String(valorA).toLowerCase();
                    const strB = String(valorB).toLowerCase();
                    if (strA < strB) return direction === "asc" ? -1 : 1;
                    if (strA > strB) return direction === "asc" ? 1 : -1;
                    return 0;
                }
                case CELLTIPO_ESTADOROW:
                case CELLTIPO_ELIMINARROW:
                case CELLTIPO_ICONOACCION:
                    return 0;
                default: {
                    const numA = parseFloat(valorA) || 0;
                    const numB = parseFloat(valorB) || 0;
                    return direction === "asc" ? numA - numB : numB - numA;
                }
            }
        };
    };

    // ************************************************
    // RENDER HELPERS (reintroducidos)
    // ************************************************

    renderEstado = (numFila, columna, elemento) => {
        return (
            <div
                style={{ cursor: "pointer" }}
                onClick={() => this.setState({ elementoSeleccionadoCodigo: elemento.codigo })}
            >
                {numFila+1}
            </div>
        );
    }

    renderEliminar = (numFila, columna, elemento) => {
        const { onEliminar } = this.props;
        return (
            <div
                style={{ cursor: "pointer", fontSize: "18px", textAlign: "center" }}
                onClick={() => { onEliminar && onEliminar(elemento); }}
            >
                <Icon icon={"trash"} />
            </div>
        );
    }

    renderCadena = (numFila, columna, elemento) => {
        let valor = getValorCampo(elemento, columna.campo, "");
        return (<div>{valor}</div>);
    }

    renderMemo = (numFila, columna, elemento) => {
        let valor = elemento[columna.campo];
        return (<div>{valor}</div>);
    }

    renderFecha = (numFila, columna, elemento) => {
        let valor = elemento[columna.campo];
        return (
            <div>
                {valor ? new Date(valor).toLocaleDateString("es-ES", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                }) : ""}
            </div>
        );
    }

    renderIconoAccion = (numFila, columna, elemento) => {
        let icono = columna.icon;
        let onAccion = columna.onAccion;
        return (
            <div
                style={{ cursor: "pointer", fontSize: "18px", textAlign: "center" }}
                onClick={() => { onAccion && onAccion(elemento); }}
            >
                <Icon icon={icono} />
            </div>
        );
    }

    // ************************************************
    // RENDER (encabezado, filas y celdas) con sticky left
    // ************************************************

    renderEncabezado = ()  => {
        const { columnas, colWidths } = this.state;
        const { encabezadoFontSize } = this.props;
        let encabezadoFontSizeReal = (encabezadoFontSize ? encabezadoFontSize : 12) + "px";

        // importante: resetear left acumulado antes de empezar
        let acumuladoLeft = 0;

        return (
            <thead>
            <tr>
                {columnas.map((columna, index) => {
                    let estiloSticky = {};
                    if (columna.fija) {
                        estiloSticky = {
                            position: "sticky",
                            left: acumuladoLeft,
                            zIndex: 10, // más alto que las celdas de datos
                            backgroundColor: "#eee",
                        };
                        acumuladoLeft += this.state.colWidths[index];
                    }

                    return (
                        <th
                            key={columna.campo + "_head"}
                            style={{
                                ...estiloSticky,
                                border: "1px solid #ccc",
                                width: colWidths[index],
                                padding: "8px",
                                fontSize: encabezadoFontSizeReal,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                position: "sticky",
                                top: 0, // se mantiene fijo al hacer scroll vertical
                                zIndex: columna.fija ? 11 : 9, // para que se superponga correctamente
                                backgroundColor: "#eee",
                                userSelect: "none",
                                cursor: "default",
                            }}
                            onMouseMove={(e) => this.handleMouseMoveTh(e, index)}
                            onMouseLeave={() => this.setState({ hoverCol: null })}
                            onMouseDown={(e) => this.handleMouseDownTh(e, index)}
                            onDoubleClick={(e) => this.autoResizeCol(e, index)}
                            onClick={() => this.handleSort(columna)}
                        >
                            {columna.titulo}
                            {this.state.sortConfig?.campo === columna.campo && (
                                <span style={{ marginLeft: "4px" }}>
                  {this.state.sortConfig.direction === "asc" ? "▲" : "▼"}
                </span>
                            )}
                        </th>
                    );
                })}
            </tr>
            </thead>
        );
    };


    renderColumnaDato = (numFila, columna, elemento, indexColumna, estiloSticky = {}) => {
        const {campoClave, fontSize} = this.props;
        const {colWidths} = this.state;

        let contenido;
        switch (columna.tipo) {
            case CELLTIPO_ESTADOROW:
                contenido = this.renderEstado(numFila, columna, elemento);
                break;
            case CELLTIPO_ELIMINARROW:
                contenido = this.renderEliminar(numFila, columna, elemento);
                break;
            case CELLTIPO_CADENA:
                contenido = this.renderCadena(numFila, columna, elemento);
                break;
            case CELLTIPO_FECHA:
                contenido = this.renderFecha(numFila, columna, elemento);
                break;
            case CELLTIPO_MEMO:
                contenido = this.renderMemo(numFila, columna, elemento);
                break;
            case CELLTIPO_ICONOACCION:
                contenido = this.renderIconoAccion(numFila, columna, elemento);
                break;
            default:
                contenido = null;
        }

        let fontSizeReal= (fontSize ? fontSize: 12) + "px";
        let estiloBase = {
            border: "1px solid #ccc",
            padding: "8px",
            verticalAlign: "top",
            width: colWidths[indexColumna],
            fontSize: fontSizeReal,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap"
        };

        if (columna.tipo === CELLTIPO_MEMO) {
            estiloBase.whiteSpace = "pre-wrap";
            estiloBase.wordBreak = "break-word";
            estiloBase.maxWidth = "300px";
        }
        if (columna.tipo === CELLTIPO_ICONOACCION) {
            estiloBase.textAlign = "center";
        }
        if (columna.tipo === CELLTIPO_ESTADOROW) {
            estiloBase.textAlign = "center";
            estiloBase.backgroundColor = "lightgrey";
        }

        let estiloFinal = { ...estiloBase, ...estiloSticky };

        let campoClaveReal= campoClave ? campoClave : "codigo";
        let claveElemento= columna.campo + "_" + elemento[campoClaveReal];
        return (
            <td key={claveElemento} style={estiloFinal}>
                {contenido}
            </td>
        );
    };

    renderDatos= ()  => {
        const {columnas, datos} = this.state;
        const {filasZebra, filasZebraColores, estilizadorRow} = this.props;

        const filasZebraReal = (filasZebra === undefined ? true : filasZebra);
        const filasZebraColoresReal = filasZebraColores && filasZebraColores.length === 2 ? filasZebraColores : filasZebraColoresDefecto;

        return datos.map ((elemento, indexRow) => {
            let estiloRow = { verticalAlign: "top" };
            if (filasZebraReal)
                estiloRow.backgroundColor = (indexRow % 2 === 0) ? filasZebraColoresReal[0]: filasZebraColoresReal[1];

            if (typeof estilizadorRow === "function") {
                const estiloExtra = estilizadorRow(elemento, indexRow);
                if (estiloExtra) {
                    estiloRow = { ...estiloRow, ...estiloExtra };
                }
            }

            let acumuladoLeft = 0;

            return (
                <tr key={elemento.codigo} style={estiloRow}>
                    {
                        columnas.map(( columna, indexColumna) => {
                            let estiloSticky = {};
                            if (columna.fija) {
                                estiloSticky = {
                                    position: "sticky",
                                    left: acumuladoLeft,
                                    zIndex: 5,
                                    backgroundColor: estiloRow.backgroundColor || "#fff"
                                };
                                acumuladoLeft += this.state.colWidths[indexColumna];
                            }
                            return this.renderColumnaDato(indexRow, columna, elemento, indexColumna, estiloSticky);
                        })
                    }
                </tr>
            )
        });
    }

    render() {
        const {ancho, alto} = this.props;
        let estiloEnvolvente= {
            overflow: "auto",
            border: "1px solid #ccc",
            padding: "2px"
        }
        if (ancho) estiloEnvolvente.width= ancho;
        if (alto) estiloEnvolvente.height= alto;

        return (
            <div style={estiloEnvolvente} >
                <table style={{borderCollapse: "collapse", width: "100%", tableLayout: "fixed" }}>
                    {this.renderEncabezado()}
                    <tbody>
                    {this.renderDatos()}
                    </tbody>
                </table>
            </div>
        );
    }
}

Tabla.propTypes = {
    datos: PropTypes.array,
    columnas: PropTypes.array,
    campoClave: PropTypes.string,
    encabezadoFontSize: PropTypes.number,
    fontSize: PropTypes.number,
    ancho: PropTypes.number,
    alto: PropTypes.number,
    estilizadorRow: PropTypes.func,
    filasZebra: PropTypes.bool,
    filasZebraColores: PropTypes.arrayOf(PropTypes.string),
    mostrarEliminar: PropTypes.bool,
    onEliminar: PropTypes.func,
}

export default Tabla
